//
// Copyright © 2023 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Class, Ref, Doc, SearchResultDoc, TxOperations } from '@hcengineering/core'
import { type ObjectSearchCategory } from './types'
import plugin from './plugin'
import { getClient } from './utils'

interface SearchSection {
  category: ObjectSearchCategory
  items: SearchResultDoc[]
}

/**
 * @public
 */
export interface SearchItem {
  num: number
  item: SearchResultDoc
  category: ObjectSearchCategory
}

function packSearchResultsForListView (sections: SearchSection[]): SearchItem[] {
  let results: SearchItem[] = []
  for (const section of sections) {
    const category = section.category
    if (category.classToSearch === undefined) continue

    const seen = new Set<Ref<Doc>>()
    const items = section.items.filter((item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })

    results = results.concat(
      items.map((item, num) => {
        return { num, category, item }
      })
    )
  }
  return results
}

function mergeSearchSectionsByCategory (sections: SearchSection[]): SearchSection[] {
  const sectionsByCategory = new Map<Ref<ObjectSearchCategory>, SearchSection>()
  const result: SearchSection[] = []

  for (const section of sections) {
    const existing = sectionsByCategory.get(section.category._id)
    if (existing !== undefined) {
      existing.items.push(...section.items)
      continue
    }

    const merged = { ...section, items: [...section.items] }
    sectionsByCategory.set(section.category._id, merged)
    result.push(merged)
  }

  return result
}

function isIdentifierLikeQuery (query: string): boolean {
  return getIdentifierSearchPattern(query) !== undefined
}

function getIdentifierSearchPattern (query: string): string | undefined {
  const normalized = query.trim().toUpperCase()
  if (/^[A-Z][A-Z0-9]{1,5}$/.test(normalized)) {
    return `${normalized}%`
  }
  if (/^\S+-\d*$/.test(normalized)) {
    return `${normalized}%`
  }
  if (/^\d+$/.test(normalized)) {
    return `%${normalized}%`
  }
}

function getExactIdentifier (query: string): string | undefined {
  const normalized = query.trim().toUpperCase()
  return /^\S+-\d+$/.test(normalized) ? normalized : undefined
}

function canSearchByIdentifier (client: TxOperations, category: ObjectSearchCategory): boolean {
  return (
    category.classToSearch !== undefined &&
    client.getHierarchy().findAttribute(category.classToSearch, 'identifier') !== undefined
  )
}

function isExactIdentifierResult (item: SearchResultDoc, query: string): boolean {
  const identifier = (item.doc as any).identifier
  return typeof identifier === 'string' && identifier.toUpperCase() === query.trim().toUpperCase()
}

function rankIdentifierSections (sections: SearchSection[], query: string): SearchSection[] {
  return sections.map((section) => {
    const exactItems = section.items.filter((item) => isExactIdentifierResult(item, query))
    const otherItems = section.items.filter((item) => !isExactIdentifierResult(item, query))
    return { ...section, items: [...exactItems, ...otherItems] }
  })
}

async function searchIdentifierCategory (
  client: TxOperations,
  category: ObjectSearchCategory,
  query: string,
  limit?: number
): Promise<SearchSection | undefined> {
  if (!canSearchByIdentifier(client, category)) return
  const identifierPattern = getIdentifierSearchPattern(query)
  if (identifierPattern === undefined) return

  const exactIdentifier = getExactIdentifier(query)
  const exact = exactIdentifier !== undefined
    ? await client.findOne(category.classToSearch!, { identifier: exactIdentifier } as any)
    : undefined
  const docs = await client.findAll(
    category.classToSearch!,
    { identifier: { $like: identifierPattern } } as any,
    { limit: limit ?? 5 }
  )
  const allDocs = exact !== undefined ? [exact, ...docs.filter((doc) => doc._id !== exact._id)] : docs
  return {
    category,
    items: allDocs.slice(0, limit ?? 5).map((doc) => ({
      id: doc._id,
      doc,
      icon: category.icon,
      title: (doc as any).identifier
    }))
  }
}

async function doIdentifierSearch (
  client: TxOperations,
  categories: ObjectSearchCategory[],
  query: string,
  limit?: number
): Promise<SearchSection[]> {
  const resolvedSections = await Promise.all(categories.map((cat) => searchIdentifierCategory(client, cat, query, limit)))
  return resolvedSections.filter((section): section is SearchSection => section !== undefined && section.items.length > 0)
}

async function searchCategory (
  client: TxOperations,
  category: ObjectSearchCategory,
  query: string,
  limit?: number
): Promise<SearchSection | undefined> {
  if (category.classToSearch === undefined) return
  const classes =
    category.includeChilds === true
      ? client.getHierarchy().getDescendants(category.classToSearch)
      : [category.classToSearch]
  const r = await client.searchFulltext(
    {
      query: `${query}*`,
      classes
    },
    {
      limit: limit ?? 5
    }
  )
  return { category, items: r.docs }
}

async function doFulltextSearch (
  client: TxOperations,
  categories: ObjectSearchCategory[],
  query: string,
  limit?: number
): Promise<SearchSection[]> {
  const sections: SearchSection[] = []
  const promises: Array<Promise<SearchSection | undefined>> = []
  for (const cat of categories) {
    promises.push(searchCategory(client, cat, query, limit))
  }

  const resolvedSections = await Promise.all(promises)
  for (const s of resolvedSections) {
    if (s !== undefined) sections.push(s)
  }

  return sections.sort((a, b) => {
    const ac = categories.indexOf(a.category)
    const bc = categories.indexOf(b.category)
    if (ac === bc) {
      const maxScoreA = Math.max(...(a?.items ?? []).map((obj) => obj?.score ?? 0))
      const maxScoreB = Math.max(...(b?.items ?? []).map((obj) => obj?.score ?? 0))
      return maxScoreB - maxScoreA
    }
    return ac - bc
  })
}

const categoriesByContext = new Map<string, ObjectSearchCategory[]>()

export async function searchFor (
  context: 'mention' | 'spotlight',
  query: string,
  category?: Ref<ObjectSearchCategory>,
  limit?: number
): Promise<{ items: SearchItem[], query: string }> {
  const client = getClient()
  let categories = categoriesByContext.get(context)
  if (categories === undefined) {
    categories = await client.findAll(plugin.class.ObjectSearchCategory, { context })

    categories.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    categoriesByContext.set(context, categories)
  }

  if (categories === undefined) {
    return { items: [], query }
  }

  const classesToSearch: Array<Ref<Class<Doc>>> = []
  const cats = category === undefined ? categories : categories.filter((it) => it._id === category)
  for (const cat of cats) {
    if (cat.classToSearch !== undefined) {
      classesToSearch.push(cat.classToSearch)
    }
  }

  const directSections =
    context === 'spotlight' && isIdentifierLikeQuery(query)
      ? rankIdentifierSections(await doIdentifierSearch(client, cats, query, limit), query)
      : []
  const sections = await doFulltextSearch(client, cats, query, limit)
  return { items: packSearchResultsForListView(mergeSearchSectionsByCategory([...directSections, ...sections])), query }
}
